import React, {useState, useEffect, useCallback, useRef} from 'react'
import {Block, Col, Row} from 'jsxstyle'
import {SortByEnum, XpertKnowledgeDto} from '../../../services/XpertKnowledge/type'
import XpertKnowledgeRow from './XpertKnowledgeRow'
import CustomDropdown from '../../../components/CustomDropdown/CustomDropdown'
import {DateRangePicker} from 'rsuite'
import i18n from '../../../common/i18n'
import {UserDto, UserRole} from '../../../services/User/type'
import {useXpertKnowledgeContext} from '../XpertKnowledgeContext'
import Loader from '../../../components/Loader'
import posthog from 'posthog-js'

interface PublishedTutorialsSectionProps {
  tutorials: XpertKnowledgeDto[]
  selectedId?: string
  onTutorialClick: (tutorial: XpertKnowledgeDto) => void
  onCreatorChange: (creator?: UserDto) => void
  onEquipmentTypeChange: (equipmentType: string) => void
  onSortChange: (sortOption: SortByEnum) => void
  onDateRangeChange: (startDate: string, endDate: string) => void
  onDateRangeReset: () => void
  currentCreatorId?: number
  currentEquipmentType?: string
  currentSortBy?: SortByEnum
  currentStartDate?: string
  currentEndDate?: string
}

const PublishedTutorialsSection: React.FC<PublishedTutorialsSectionProps> = ({
  tutorials,
  selectedId,
  onTutorialClick,
  onCreatorChange,
  onEquipmentTypeChange,
  onSortChange,
  onDateRangeChange,
  onDateRangeReset,
  currentCreatorId,
  currentEquipmentType,
  currentSortBy,
  currentStartDate,
  currentEndDate,
}) => {
  const {isLoadingPublish, creators, equipments, fetchNextPage, hasMorePages, isLoadingNextPage, totalPublish} = useXpertKnowledgeContext()

  const [selectedCreator, setSelectedCreator] = useState<string>(i18n.t('xk.list.published.filter.allCreators'))
  const [selectedEquipmentType, setSelectedEquipmentType] = useState<string>(i18n.t('xk.list.published.filter.all'))
  const [sortOption, setSortOption] = useState<string>(i18n.t('xk.list.published.sortOptions.default'))
  const [dateRange, setDateRange] = useState<[Date, Date] | null>(null)

  const observerRef = useRef<IntersectionObserver>()
  const lastElementRef = useRef<HTMLDivElement>()

  const sortOptions = [
    i18n.t('xk.list.published.sortOptions.default'),
    i18n.t('xk.list.published.sortOptions.title'),
    i18n.t('xk.list.published.sortOptions.rating'),
    i18n.t('xk.list.published.sortOptions.views'),
  ]

  useEffect(() => {
    if (currentCreatorId) {
      const creator = creators?.find((c) => c.id === currentCreatorId)
      if (creator?.fullName) {
        setSelectedCreator(creator.fullName)
      }
    } else {
      setSelectedCreator(i18n.t('xk.list.published.filter.allCreators'))
    }
  }, [currentCreatorId, creators])

  useEffect(() => {
    if (currentEquipmentType) {
      setSelectedEquipmentType(currentEquipmentType.replace(/`/g, "'"))
    } else {
      setSelectedEquipmentType(i18n.t('xk.list.published.filter.all'))
    }
  }, [currentEquipmentType])

  useEffect(() => {
    if (currentSortBy) {
      let sortOptionText: string
      switch (currentSortBy) {
        case SortByEnum.TITLE:
          sortOptionText = i18n.t('xk.list.published.sortOptions.title')
          break
        case SortByEnum.RATING:
          sortOptionText = i18n.t('xk.list.published.sortOptions.rating')
          break
        case SortByEnum.VIEWS:
          sortOptionText = i18n.t('xk.list.published.sortOptions.views')
          break
        default:
          sortOptionText = i18n.t('xk.list.published.sortOptions.default')
      }
      setSortOption(sortOptionText)
    } else {
      setSortOption(i18n.t('xk.list.published.sortOptions.default'))
    }
  }, [currentSortBy])

  useEffect(() => {
    if (currentStartDate && currentEndDate) {
      try {
        const startDate = new Date(currentStartDate)
        const endDate = new Date(currentEndDate)
        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          setDateRange([startDate, endDate])
        }
      } catch (error) {
        console.error('Error parsing date range:', error)
        setDateRange(null)
      }
    } else {
      setDateRange(null)
    }
  }, [currentStartDate, currentEndDate])

  const lastTutorialElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoadingNextPage) return
      if (observerRef.current) observerRef.current.disconnect()

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMorePages && !isLoadingNextPage) {
            fetchNextPage()
          }
        },
        {
          threshold: 0.1,
          rootMargin: '100px',
        }
      )

      if (node) observerRef.current.observe(node)
      lastElementRef.current = node
    },
    [isLoadingNextPage, hasMorePages, fetchNextPage]
  )

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [])

  const handleCreatorSelect = (value: string) => {
    setSelectedCreator(value)
    if (value === i18n.t('xk.list.published.filter.allCreators')) {
      onCreatorChange({id: 0, fullName: 'All', activationId: 1, role: UserRole.AGENT})
    } else {
      const selectedCreatorObj = creators?.find((creator) => creator.fullName === value)
      onCreatorChange(selectedCreatorObj)
    }
  }

  const handleEquipmentTypeSelect = (value: string) => {
    setSelectedEquipmentType(value)
    onEquipmentTypeChange(value)
  }

  const handleSortChange = (value: string) => {
    setSortOption(value)

    let sortEnum: SortByEnum

    if (value === i18n.t('xk.list.published.sortOptions.default')) {
      sortEnum = SortByEnum.DEFAULT
    } else if (value === i18n.t('xk.list.published.sortOptions.title')) {
      sortEnum = SortByEnum.TITLE
    } else if (value === i18n.t('xk.list.published.sortOptions.rating')) {
      sortEnum = SortByEnum.RATING
    } else if (value === i18n.t('xk.list.published.sortOptions.views')) {
      sortEnum = SortByEnum.VIEWS
    } else {
      sortEnum = SortByEnum.DEFAULT
    }

    onSortChange(sortEnum)
  }

  const handleDateChange = (value: [Date, Date] | null, event: React.SyntheticEvent) => {
    setDateRange(value)
    if (value && value[0] && value[1]) {
      const startTimestamp = value[0].toISOString().replace('Z', '+00:00')
      const endTimestamp = value[1].toISOString().replace('Z', '+00:00')
      onDateRangeChange(startTimestamp, endTimestamp)
    }
  }

  const handleDateRangeReset = () => {
    setDateRange(null)
    onDateRangeReset()
  }

  const creatorOptionsFullname = creators?.map((creator) => creator.fullName).filter((name): name is string => !!name) || []
  creatorOptionsFullname.unshift(i18n.t('xk.list.published.filter.allCreators'))

  const equipmentOptions = [...equipments]
  equipmentOptions.unshift(i18n.t('xk.list.published.filter.all'))

  return (
    <Col gap="15px">
      <Row justifyContent="space-between" alignItems="center" padding="8px 0">
        <Block fontSize="18px" fontWeight={600}>
          {i18n.t('xk.list.published.title')}
        </Block>
        <Block fontSize="15px" fontWeight={500} padding="6px 12px" backgroundColor="#f5f5f5" border="1px solid #ddd" borderRadius="8px" color="#333">
          {i18n.t('xk.list.published.count', {count: totalPublish})}
        </Block>
      </Row>
      <Row justifyContent="space-between" alignItems="center" gap="20px">
        <Row gap="8px">
          <Col>
            <Block fontSize="16px" fontWeight={500} marginBottom="8px">
              {i18n.t('xk.list.published.filter.creator')}
            </Block>
            <CustomDropdown
              options={creatorOptionsFullname}
              value={selectedCreator}
              onChange={handleCreatorSelect}
              onClick={() =>
                posthog.capture('filter_dropdown_clicked', {
                  filter_type: 'creator name',
                })
              }
              placeholder="All Creators"
              width="220px"
            />
          </Col>
          <Col>
            <Block fontSize="16px" fontWeight={500} marginBottom="8px">
              {i18n.t('xk.list.published.filter.equipmentType')}
            </Block>
            <CustomDropdown
              options={equipmentOptions}
              value={selectedEquipmentType}
              onChange={handleEquipmentTypeSelect}
              onClick={() =>
                posthog.capture('filter_dropdown_clicked', {
                  filter_type: 'equipment type',
                })
              }
              placeholder={i18n.t('xk.list.published.filter.all')}
              width="220px"
            />
          </Col>
        </Row>
        <Row gap="12px" alignItems="flex-end">
          <Col>
            <Block fontSize="16px" fontWeight={500} marginBottom="8px">
              {i18n.t('xk.list.published.filter.date')}
            </Block>
            <DateRangePicker
              format="dd/MM/yyyy"
              character=" - "
              size="md"
              value={dateRange}
              onChange={handleDateChange}
              onClean={handleDateRangeReset}
              onClick={() =>
                posthog.capture('filter_dropdown_clicked', {
                  filter_type: 'date',
                })
              }
              placement="bottomEnd"
            />
          </Col>
          <Col>
            <Block fontSize="16px" fontWeight={500} marginBottom="8px">
              {i18n.t('xk.list.published.sort')}
            </Block>
            <CustomDropdown
              options={sortOptions}
              value={sortOption}
              onChange={handleSortChange}
              placeholder={i18n.t('xk.list.published.sortOptions.default')}
              width="140px"
            />
          </Col>
        </Row>
      </Row>

      {isLoadingPublish ? (
        <Col height="calc(100vh - 490px)" width="100%" justifyContent="center" alignItems="center">
          <Loader size={75} />
        </Col>
      ) : (
        <>
          {tutorials.length > 0 && (
            <Col gap="10px">
              {tutorials.map((tutorial, index) => {
                const isLastElement = index === tutorials.length - 1
                return (
                  <div key={tutorial.id} ref={isLastElement ? lastTutorialElementRef : undefined}>
                    <XpertKnowledgeRow
                      xpertKnowledge={{...tutorial, isSelected: tutorial.id === selectedId}}
                      onRowClick={() => {
                        posthog.capture('xpertknowledge_row_clicked', {
                          position_index: index,
                          has_equipment_type: !!tutorial.publishTutorial?.equipmentType,
                          has_rating: tutorial.publishTutorial?.avgRating != 0,
                        })
                        onTutorialClick(tutorial)
                      }}
                    />
                  </div>
                )
              })}

              {isLoadingNextPage && (
                <Col width="100%" justifyContent="center" alignItems="center" padding="20px">
                  <Loader size={40} />
                </Col>
              )}

              {!hasMorePages && tutorials.length > 0 && (
                <Col width="100%" justifyContent="center" alignItems="center" padding="20px">
                  <Block fontSize="14px" color="#666">
                    {i18n.t('xk.list.published.endOfResults')}
                  </Block>
                </Col>
              )}
            </Col>
          )}
        </>
      )}
    </Col>
  )
}

export default PublishedTutorialsSection
